import React from 'react';
import { Box, Paper, Typography, Stack, Button, Fade, CircularProgress } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { COLORS } from '../utils/constants';
// import { formatPrice } from '../../utils/formatters';
import { formatPrice } from '@/utils/pricingHelpers';

const PriceSummaryCard = ({
  handleBookConsignment,
  calculatedPrice,
  formProgress,
  isDesktop,
  handleCalculatePrice,
  destination,
  origin,
  isCalculateDisabled,
  parcelType,
  parcelWeight,
  pricingLoading,
  bookingLoading,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: `2px solid ${calculatedPrice ? COLORS.accent : COLORS.border}`,
        background: calculatedPrice
          ? `linear-gradient(135deg, ${COLORS.surface} 0%, ${COLORS.white} 100%)`
          : COLORS.white,
        transition: 'all 0.3s ease',
        position: isDesktop ? 'sticky' : 'static',
        top: isDesktop ? 100 : 'auto',
        overflow: 'hidden',
        width: isDesktop ? 'fit-content' : 'auto',
      }}
    >
      {/* Progress bar at top */}
      {!calculatedPrice && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: COLORS.surface,
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${formProgress}%`,
              background: formProgress === 100 ? COLORS.accent : COLORS.primary,
              transition: 'width 0.4s ease, background 0.3s ease',
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
          mt: !calculatedPrice ? 1 : 0,
        }}
      >
        <CalculateIcon sx={{ color: COLORS.primary, fontSize: 24 }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: COLORS.textPrimary,
            fontSize: '1.1rem',
          }}
        >
          Price Summary
        </Typography>
      </Box>

      {calculatedPrice ? (
        <Fade in>
          <Box>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff',
                mb: 2.5,
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.25)',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1.5px',
                }}
              >
                Estimated Price
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2.25rem', md: '2.75rem' },
                  letterSpacing: '-1px',
                  color: '#ffffff',
                  mt: 0.5,
                }}
              >
                {formatPrice(calculatedPrice.finalPrice)}
              </Typography>
            </Box>

            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                  Distance
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.textPrimary }}>
                  {calculatedPrice.distance.toFixed(1)} km
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                  Weight
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.textPrimary }}>
                  {parcelWeight < 1 ? `${(parcelWeight * 1000).toFixed(0)}g` : `${parcelWeight}kg`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                  Package Type
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.textPrimary }}>
                  {parcelType.join(', ')}
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={1.5}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCalculatePrice}
                disabled={pricingLoading}
                startIcon={pricingLoading ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: COLORS.primary,
                  color: COLORS.primary,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: COLORS.primary,
                    backgroundColor: `${COLORS.primary}08`,
                  },
                }}
              >
                {pricingLoading ? 'Calculating...' : 'Recalculate'}
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleBookConsignment}
                disabled={bookingLoading}
                endIcon={bookingLoading ? null : <ArrowForwardIcon />}
                startIcon={bookingLoading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  py: 1.75,
                  borderRadius: 2,
                  background: COLORS.primary,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': {
                    background: COLORS.primaryLight,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  },
                  '&:disabled': {
                    background: COLORS.border,
                  },
                }}
              >
                {bookingLoading ? 'Processing...' : 'Proceed to Booking'}
              </Button>
            </Stack>
          </Box>
        </Fade>
      ) : (
        <Box>
          {/* Progress steps */}
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: origin ? COLORS.accent : COLORS.border,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                }}
              >
                {origin && (
                  <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>
                    ✓
                  </Typography>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: origin ? COLORS.textPrimary : COLORS.textSecondary,
                  fontWeight: origin ? 600 : 400,
                  fontSize: '0.85rem',
                }}
              >
                Pickup location
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: destination ? COLORS.accent : COLORS.border,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                }}
              >
                {destination && (
                  <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>
                    ✓
                  </Typography>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: destination ? COLORS.textPrimary : COLORS.textSecondary,
                  fontWeight: destination ? 600 : 400,
                  fontSize: '0.85rem',
                }}
              >
                Drop-off location
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: parcelWeight ? '#10b981' : COLORS.border,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                }}
              >
                {parcelWeight && (
                  <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>
                    ✓
                  </Typography>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: parcelWeight ? '#10b981' : COLORS.textSecondary,
                  fontWeight: parcelWeight ? 700 : 400,
                  fontSize: '0.85rem',
                }}
              >
                Package weight{' '}
                {parcelWeight
                  ? `(${parcelWeight < 1 ? `${(parcelWeight * 1000).toFixed(0)}g` : `${parcelWeight}kg`})`
                  : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: parcelType.length > 0 ? COLORS.accent : COLORS.border,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                }}
              >
                {parcelType.length > 0 && (
                  <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>
                    ✓
                  </Typography>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: parcelType.length > 0 ? COLORS.textPrimary : COLORS.textSecondary,
                  fontWeight: parcelType.length > 0 ? 600 : 400,
                  fontSize: '0.85rem',
                }}
              >
                Package type
              </Typography>
            </Box>
          </Stack>

          {formProgress === 100 && (
            <Fade in>
              <Typography
                variant="body2"
                sx={{
                  color: COLORS.accent,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                ✨ Ready to calculate price!
              </Typography>
            </Fade>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={handleCalculatePrice}
            disabled={isCalculateDisabled || pricingLoading}
            endIcon={pricingLoading ? null : <ArrowForwardIcon />}
            startIcon={pricingLoading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              py: 1.75,
              borderRadius: 2,
              background: formProgress === 100 ? COLORS.accent : COLORS.primary,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 700,
              boxShadow: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: formProgress === 100 ? '#059669' : COLORS.primaryLight,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              '&:disabled': {
                background: COLORS.border,
                color: COLORS.textTertiary,
              },
            }}
          >
            {pricingLoading ? 'Calculating...' : 'Calculate Price'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default PriceSummaryCard;
